/**
 * SCHOOL_ADMIN dietary-accommodation intake — `/api/special-diet/*`.
 *
 * Distinct from `api/special-diet.ts`, which is the PARENT-facing Plan-42
 * surface (retiring) plus the eligible-items sidecar. This module is the
 * school-side intake: upload the note the office received, list what has been
 * submitted, download a note back.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authHeaders } from './http'

/** Mirrors `DOCTOR_NOTE_MAX_BYTES` on the server. Client-side pre-check only. */
export const NOTE_MAX_BYTES = 10 * 1024 * 1024

/** Mirrors the server allowlist; the server re-checks by magic bytes. */
/** Re-exported so there is ONE accept list, shared with the Menus wizard. */
// From @ordo/constants, NOT @ordo/schema: this is a CLIENT module, and
// importing the schema package here pulls the generated Prisma client into the
// browser bundle (it did — build-ordo failed on externalized node:path).
export { DOCTOR_NOTE_FILE_ACCEPT as NOTE_ACCEPT } from '@ordo/constants/doctor-note-upload'

export interface SpecialDietAccommodationWire {
  revision?: number
  supersedesId?: string | null
  successor?: { id: string; revision: number; status: string } | null
  updatedAt?: string
  notes?: string | null
  acceptableMilkOptionCodes?: string[]
  parentOrderingEnabled?: boolean | null
  parentContactEmail?: string | null
  parentContactFirstName?: string | null
  parentContactLastName?: string | null
  id: string
  /**
   * NULL for the TYPED and UNNAMED identities — a school may hold a note for a
   * child the roster has not caught up with. See `special-diet.prisma`.
   */
  studentUserId: string | null
  /** The name the office typed when nothing matched. Display only. */
  unlinkedStudentName: string | null
  title: string
  status: string
  workflowStatus: string
  restrictedAllergens: string[]
  dietaryNeeds: string[]
  mealtimes: string[]
  effectiveFrom: string
  effectiveUntil: string | null
  menuLiveEstimateStart: string | null
  menuLiveEstimateEnd: string | null
  doctorNoteDocumentId: string | null
  student?: { firstName?: string | null; lastName?: string | null } | null
}

export interface SubmitAccommodationInput {
  parentOrderingEnabled?: boolean
  newStudent?: { firstName: string; lastName: string; localStudentId?: string; grade?: string; termId?: string }
  parentContactFirstName?: string | null
  parentContactLastName?: string | null
  parentContactEmail?: string | null
  acceptableMilkOptionCodes?: string[]
  intakeKey?: string

  organizationId: string
  locationId?: string | null
  studentUserId: string | null
  /**
   * Optional: the server derives it from the picked restrictions when absent,
   * so the label and the structured data cannot disagree. The intake stopped
   * asking for it once the restriction became a tag picker.
   */
  title?: string
  mealtimes: string[]
  /** Optional: defaults to today. A note handed to the office is in force now. */
  effectiveFrom?: string
  effectiveUntil?: string | null
  restrictedAllergens?: string[]
  dietaryNeeds?: string[]
  /** Free text, only when nothing in the canonical tag list fits. */
  customRestriction?: string | null
  notes?: string | null
  note: { fileName: string; contentBase64: string }
}

export function schoolAccommodationsKey(args: { organizationId?: string | null; locationId?: string | null }) {
  return ['special-diet', 'accommodations', args] as const
}

async function readError(res: Response): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { message?: string; error?: string }
  throw new Error(body.message ?? body.error ?? 'Something went wrong. Please try again.')
}

export function useSchoolAccommodationsQuery(args: { organizationId?: string | null; locationId?: string | null }) {
  const params = new URLSearchParams()
  if (args.organizationId) {
    params.set('organizationId', args.organizationId)
  }
  if (args.locationId) {
    params.set('locationId', args.locationId)
  }
  const qs = params.toString()
  return useQuery({
    queryKey: schoolAccommodationsKey(args),
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/special-diet/accommodations${qs ? `?${qs}` : ''}`, {
        signal,
        headers: authHeaders(),
      })
      if (!res.ok) {
        await readError(res)
      }
      return (await res.json()) as { accommodations: SpecialDietAccommodationWire[]; revisionsEnabled?: boolean }
    },
    enabled: Boolean(args.organizationId),
    staleTime: 30_000,
  })
}

export function useSubmitAccommodationMutation(args: { organizationId?: string | null; locationId?: string | null }) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SubmitAccommodationInput) => {
      const res = await fetch('/api/special-diet/accommodations', {
        method: 'POST',
        headers: { ...authHeaders(), 'content-type': 'application/json' },
        body: JSON.stringify(input),
      })
      if (!res.ok) {
        await readError(res)
      }
      return (await res.json()) as { accommodation: SpecialDietAccommodationWire }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schoolAccommodationsKey(args) })
    },
  })
}

export interface StudentSearchResultWire {
  userId: string
  name: string
  grade: string | null
  studentId: string | null
}

/**
 * Find a student at this campus by NAME. The office recording a note has the
 * name, not a cuid, and often no student number either — so the search is by
 * name and the admin disambiguates from whatever identifiers the campus has.
 *
 * `ambiguous` is true when two results share a name AND neither carries a
 * grade or student number, i.e. the admin genuinely cannot tell them apart
 * and needs to go find one.
 *
 * `campusHasStudents` is false when this campus has no roster at all — a
 * different problem from a name that does not match, and one the empty state
 * must not describe as a spelling mistake.
 */
export function useStudentSearchQuery(args: { locationId: string; search: string }) {
  const trimmed = args.search.trim()
  const search = trimmed.length >= 2 ? trimmed : ''
  return useQuery({
    queryKey: ['special-diet', 'students', args.locationId, search] as const,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ locationId: args.locationId, search })
      const res = await fetch(`/api/special-diet/students?${params.toString()}`, { signal, headers: authHeaders() })
      if (!res.ok) {
        await readError(res)
      }
      return (await res.json()) as {
        students: StudentSearchResultWire[]
        ambiguous: boolean
        campusHasStudents?: boolean
      }
    },
    enabled: args.locationId.length > 0,
    staleTime: 30_000,
  })
}

export interface DietaryTagOption {
  id: string
  slug: string
  name: string
  kind: 'ALLERGEN' | 'DIETARY'
}

export interface NoteOptions {
  grades?: string[]
  terms?: Array<{ id: string; name: string; startDate: string; endDate: string }>
  specialDietsEnabled?: boolean
  milkOptions?: Array<{ code: string; label: string; standard: boolean }>

  /** Only the meals this campus actually serves, chronologically. */
  mealtimes: string[]
  allergens: DietaryTagOption[]
  dietary: DietaryTagOption[]
}

/**
 * Everything the dietary-note dialog needs to render its choices.
 *
 * One call rather than three: the dialog cannot be filled in usefully until
 * all of it has arrived, so staging them would only produce a form that
 * changes shape while the office is reading it.
 */
export function useNoteOptionsQuery(locationId: string) {
  return useQuery({
    queryKey: ['special-diet', 'note-options', locationId] as const,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ locationId })
      const res = await fetch(`/api/special-diet/note-options?${params.toString()}`, { signal, headers: authHeaders() })
      if (!res.ok) {
        await readError(res)
      }
      return (await res.json()) as NoteOptions
    },
    enabled: locationId.length > 0,
    // The canonical tag list and a campus's service lines both change on the
    // order of months, not minutes.
    staleTime: 5 * 60_000,
  })
}

/**
 * A parent's status-only read of their own student's accommodations.
 *
 * The server projection deliberately omits the note id, reviewer notes, and
 * internal attribution — see `listForStudentAsParent`. There is no write here:
 * notes come from the school office, not the family.
 */
export function useStudentAccommodationsQuery(studentUserId: string) {
  return useQuery({
    queryKey: ['special-diet', 'accommodations', 'student', studentUserId] as const,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ studentUserId })
      const res = await fetch(`/api/special-diet/accommodations?${params.toString()}`, {
        signal,
        headers: authHeaders(),
      })
      if (!res.ok) {
        await readError(res)
      }
      return (await res.json()) as {
        accommodations: SpecialDietAccommodationWire[]
        orders?: Array<{
          id: string
          quantity: number
          status: string
          menuItem: { name: string }
          batch: { serviceDate: string; mealtime: string; status: string }
        }>
      }
    },
    enabled: studentUserId.length > 0,
    staleTime: 30_000,
  })
}

/** One-line disambiguator for a search result, or null when there is nothing to add. */
export function studentDisambiguator(row: StudentSearchResultWire): string | null {
  const parts = [row.grade ? `Grade ${row.grade}` : null, row.studentId ? `ID ${row.studentId}` : null].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** The download is a plain authenticated GET — no presign, bytes come from the DB. */
export function noteDownloadHref(documentId: string): string {
  return `/api/special-diet/documents/${encodeURIComponent(documentId)}/download`
}

// ---------------------------------------------------------------------------
// Pure display helpers (unit-tested)
// ---------------------------------------------------------------------------

/**
 * Parent/staff-facing copy for the review states. Deliberately says nothing
 * about WHO is reviewing or why — the school submitted it, and the useful
 * answer is "when can this student order".
 */
export function accommodationStatusCopy(status: string, workflowStatus: string): string {
  if (status === 'ARCHIVED') {
    return 'Archived'
  }
  if (status === 'ACTIVE') {
    return 'Ready to order'
  }
  if (status === 'REVOKED' || workflowStatus === 'DECLINED') {
    return 'Not approved'
  }
  if (status === 'EXPIRED') {
    return 'Expired'
  }
  if (workflowStatus === 'NEEDS_SCHOOL_INFO') {
    return 'More info needed'
  }
  if (workflowStatus === 'PENDING_MENU') {
    // Deliberately blunt: the child cannot order in this state, and a school
    // reading "In review" would have no idea. Ordo owes them the menu.
    return 'Menu being prepared'
  }
  return 'In review'
}

/** "Sep 1 – Sep 15" for the menu-live estimate, or null when unknown. */
export function menuLiveEstimateLabel(start: string | null, end: string | null): string | null {
  if (!start || !end) {
    return null
  }
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
  return `${fmt(start)} – ${fmt(end)}`
}

/** Client-side size pre-check so a 10 MB upload fails before it is sent. */
export function noteFileTooLarge(file: { size: number }): boolean {
  return file.size > NOTE_MAX_BYTES
}
